import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  Firestore
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Asegurar que Firestore esté disponible
const ensureFirestore = (): Firestore => {
  if (!db) {
    throw new Error('Firestore database is not initialized');
  }
  return db;
};

// Tipos de contadores
export enum CounterType {
  MAINTENANCE = 'maintenance',
  SALES = 'sales',
  INVENTORY = 'inventory',
  INVOICES = 'invoices'
}

// Inicializar un contador si no existe
export const initializeCounter = async (counterType: CounterType | string): Promise<void> => {
  try {
    const firestore = ensureFirestore();
    const counterRef = doc(firestore, 'counters', counterType);
    const counterDoc = await getDoc(counterRef);

    if (!counterDoc.exists()) {
      await setDoc(counterRef, { value: 0 });
    }
  } catch (error) {
    console.error(`Error initializing ${counterType} counter:`, error);
    throw error;
  }
};

// Obtener el valor actual de un contador
export const getCounterValue = async (counterType: CounterType): Promise<number> => {
  try {
    const firestore = ensureFirestore();
    const counterRef = doc(firestore, 'counters', counterType);
    const counterDoc = await getDoc(counterRef);

    if (!counterDoc.exists()) {
      await initializeCounter(counterType);
      return 0;
    }

    const data = counterDoc.data();
    if (!data) {
      throw new Error(`Counter ${counterType} data is undefined`);
    }
    return data.value;
  } catch (error) {
    console.error(`Error getting ${counterType} counter value:`, error);
    throw error;
  }
};

// Incrementar un contador y devolver el nuevo valor
export const incrementCounter = async (counterType: CounterType | string): Promise<number> => {
  try {
    const firestore = ensureFirestore();
    const counterRef = doc(firestore, 'counters', counterType);

    // Verificar si el contador existe
    const counterDoc = await getDoc(counterRef);
    if (!counterDoc.exists()) {
      await initializeCounter(counterType);
    }

    // Incrementar el contador
    await updateDoc(counterRef, { value: increment(1) });

    // Obtener el nuevo valor
    const updatedDoc = await getDoc(counterRef);
    const data = updatedDoc.data();
    if (!data) {
      throw new Error(`Counter ${counterType} not found after increment`);
    }
    return data.value;
  } catch (error) {
    console.error(`Error incrementing ${counterType} counter:`, error);
    throw error;
  }
};

// Establecer un valor específico para un contador
export const setCounterValue = async (counterType: CounterType, value: number): Promise<void> => {
  try {
    const firestore = ensureFirestore();
    const counterRef = doc(firestore, 'counters', counterType);

    await setDoc(counterRef, { value }, { merge: true });
  } catch (error) {
    console.error(`Error setting ${counterType} counter value:`, error);
    throw error;
  }
};
